import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { categories, MAIN_SITE_URL } from "@/mocks/categories";

const NAV_ITEMS = [
  { key: "common.nav.home", to: "/" },
  { key: "common.nav.categories", to: "/#categories" },
  { key: "common.nav.guides", to: "/#guides" },
  { key: "common.nav.shoppingGuide", to: "/guides/allchinabuy-buying-guide" },
  { key: "common.nav.safety", to: "/#faq" },
];

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 8);
    }
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  return (
    <header className="sticky top-0 z-40">
      {/* Announcement bar */}
      <div className="bg-primary-600 px-4 py-2 text-center text-xs font-semibold text-white">
        <span className="whitespace-nowrap">{t("common.disclaimer")}</span>
      </div>

      {/* Main nav */}
      <nav
        className={`w-full border-b bg-white transition-shadow ${
          scrolled ? "border-background-200 shadow-sm" : "border-background-200"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 md:px-6">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-white">
              <i className="ri-file-list-3-line text-lg" aria-hidden="true"></i>
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-heading text-base font-bold text-foreground-950">
                {t("common.siteName")}
              </span>
              <span className="text-[11px] font-medium text-foreground-500">
                {t("common.siteTagline")}
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden items-center gap-1 lg:flex">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                className="whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:bg-primary-50 hover:text-primary-700 cursor-pointer"
              >
                {t(item.key)}
              </Link>
            ))}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            <div className="hidden sm:block">
              <LanguageSwitcher />
            </div>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="hidden whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 sm:inline-flex cursor-pointer"
            >
              {t("common.buttons.buyNow")}
            </a>
            {/* Hamburger */}
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-background-300 text-foreground-900 lg:hidden cursor-pointer"
              aria-label="Menu"
            >
              <i
                className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}
                aria-hidden="true"
              ></i>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="max-h-[calc(100vh-8rem)] overflow-y-auto border-t border-background-200 bg-white px-4 pb-6 pt-2 lg:hidden">
            <div className="flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.key}
                  to={item.to}
                  className="whitespace-nowrap rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-primary-50 cursor-pointer"
                >
                  {t(item.key)}
                </Link>
              ))}

              {/* Mobile category quick links */}
              <div className="mt-3 border-t border-background-100 pt-4">
                <h4 className="px-3 text-xs font-bold uppercase tracking-wide text-foreground-400">
                  {t("common.footer.categoriesLabel")}
                </h4>
                <div className="mt-2 grid grid-cols-2 gap-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.key}
                      href={cat.link}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 cursor-pointer"
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
                        <i className="ri-arrow-right-up-line text-xs" aria-hidden="true"></i>
                      </span>
                      <span className="truncate">{t(cat.key)}</span>
                    </a>
                  ))}
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-100 pt-4">
                <LanguageSwitcher />
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-600 cursor-pointer"
                >
                  {t("common.buttons.buyNow")}
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}